import React, { useEffect, useState } from 'react';
import logo from './img/logo-comptelio.png';
import ChecklistFloatingBanner from './components/ChecklistFloatingBanner';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches;

const CheckIcon = ({ size = 12, width = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12l5 5L20 6" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const ClotereComptables = () => {
  const year = new Date().getFullYear();
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  // Widget de qualification du hero : les 3 étapes défilent en boucle, sauf si
  // l'utilisateur a demandé moins d'animations (on les affiche alors empilées).
  const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
  const [qualStep, setQualStep] = useState(0);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduceMotion(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setInterval(() => setQualStep((s) => (s + 1) % 3), 3200);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const stepClass = (i) => {
    if (reduceMotion || i === qualStep) return 'qual-num on';
    return i < qualStep ? 'qual-num done' : 'qual-num';
  };
  const panelClass = (i) => (reduceMotion || i === qualStep ? 'qual-panel on' : 'qual-panel');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const f = e.target;
    const name = f.name.value.trim();
    const city = f.city.value.trim();
    const email = f.email.value.trim();
    const phone = f.phone.value.trim();

    setStatus('sending');
    try {
      const res = await fetch('/.netlify/functions/contact-comptable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, city, email, phone }),
      });
      if (!res.ok) throw new Error('request-failed');
      setStatus('sent');
      f.reset();
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,500;6..72,600&family=Manrope:wght@500;700;800&family=Inter:wght@400;500;600;700&display=swap');

        :root{
          --paper:#FAF9F6;
          --surface:#FFFFFF;
          --surface-2:#F1F4F1;
          --ink:#16241E;
          --ink-soft:#4B5B54;
          --ink-faint:#7C8B84;
          --accent:#146C43;
          --accent-strong:#0E4F31;
          --accent-soft:#E3EFE7;
          --gold:#B08A4E;
          --gold-soft:#F3ECDF;
          --line:#DEE5E0;
          --mark-bg:#DCEFA0;
          --mark-ink:#1C2A12;
          --radius-s:8px;
          --radius-m:14px;
          --radius-l:22px;
          --shadow: 0 1px 2px rgba(20,36,28,.04), 0 10px 30px -12px rgba(20,36,28,.14);
          color-scheme: light;
        }
        /* Thème toujours clair : on n'active plus le mode sombre automatiquement
           selon les préférences système (ça faisait passer le fond en quasi-noir
           sur les téléphones réglés en mode sombre). */
        :root[data-theme="dark"]{
          --paper:#0E1512;
          --surface:#131C17;
          --surface-2:#182620;
          --ink:#EAF1EC;
          --ink-soft:#A9BBB1;
          --ink-faint:#77897F;
          --accent:#3FB27F;
          --accent-strong:#6FCB9C;
          --accent-soft:#16291F;
          --gold:#D3AE6F;
          --gold-soft:#241E12;
          --line:#22322A;
          --mark-bg:#B9DB6B;
          --mark-ink:#1C2A12;
          --shadow: 0 1px 2px rgba(0,0,0,.3), 0 18px 40px -16px rgba(0,0,0,.55);
        }

        .clotere-comptables *{box-sizing:border-box;}
        .clotere-comptables{
          background:var(--paper);
          color:var(--ink);
          font-family:'Inter',system-ui,-apple-system,sans-serif;
          line-height:1.55;
        }
        .clotere-comptables img{max-width:100%;display:block;}
        .clotere-comptables h1,.clotere-comptables h2,.clotere-comptables h3,.clotere-comptables h4{
          font-family:'Manrope',system-ui,sans-serif;
          color:var(--ink);
          margin:0;
          text-wrap:balance;
        }
        .clotere-comptables p{margin:0;color:var(--ink-soft);}
        .clotere-comptables a{color:inherit;}
        .clotere-comptables .wrap{
          max-width:85%;
          margin-inline:auto;
          padding-inline:clamp(18px,4vw,40px);
        }
        .clotere-comptables section{padding-block:clamp(56px,8vw,104px);}
        .clotere-comptables .serif{font-family:'Newsreader',Georgia,serif;font-weight:500;}
        .clotere-comptables .mark{
          background:var(--mark-bg);color:var(--mark-ink);
          padding:.03em .18em;border-radius:5px;
          box-decoration-break:clone;-webkit-box-decoration-break:clone;
        }
        .clotere-comptables .eyebrow{
          display:inline-flex;align-items:center;gap:8px;
          font-size:13px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
          color:var(--accent-strong);
          background:var(--accent-soft);
          padding:6px 14px;border-radius:999px;
        }
        .clotere-comptables .btn{
          display:inline-flex;align-items:center;justify-content:center;gap:8px;
          font-family:'Inter',sans-serif;font-weight:600;font-size:15.5px;
          padding:13px 24px;border-radius:999px;border:1px solid transparent;
          cursor:pointer;text-decoration:none;transition:transform .15s ease, opacity .15s ease, background .15s ease;
          white-space:nowrap;
        }
        .clotere-comptables .btn:active{transform:scale(.98);}
        .clotere-comptables .btn-primary{background:var(--accent);color:#fff;}
        .clotere-comptables .btn-primary:hover{background:var(--accent-strong);}
        .clotere-comptables .btn-dark{background:var(--ink);color:var(--paper);}
        .clotere-comptables .btn-dark:hover{background:var(--accent-strong);}
        .clotere-comptables .btn-ghost{background:transparent;color:var(--ink);border-color:var(--line);}
        .clotere-comptables .btn-ghost:hover{background:var(--surface-2);}
        .clotere-comptables .btn-block{width:100%;}

        /* Header */
        .clotere-comptables header{
          position:sticky;top:env(safe-area-inset-top,0px);z-index:40;
          background:color-mix(in srgb, var(--paper) 88%, transparent);
          backdrop-filter:blur(10px);
          border-bottom:1px solid var(--line);
        }
        .clotere-comptables .nav{
          display:flex;align-items:center;justify-content:space-between;
          padding-block:16px;
        }
        .clotere-comptables .brand{display:flex;align-items:center;gap:9px;font-family:'Manrope';font-weight:800;font-size:18px;letter-spacing:-.01em;}
        .clotere-comptables .brand-mark{
          width:32px;height:32px;border-radius:8px;flex:none;display:block;
        }
        .clotere-comptables .brand-sub{font-weight:500;font-size:12.5px;color:var(--ink-faint);margin-left:2px;}
        .clotere-comptables .nav-links{display:flex;align-items:center;gap:28px;}
        .clotere-comptables .nav-links a{font-size:14.5px;font-weight:500;color:var(--ink-soft);text-decoration:none;}
        .clotere-comptables .nav-links a:hover{color:var(--ink);}
        .clotere-comptables .nav-cta{display:flex;align-items:center;gap:10px;}
        @media (max-width:760px){ .clotere-comptables .nav-links{display:none;} }
        .clotere-comptables .nav-cta .cta-short{display:none;}
        @media (max-width:480px){
          .clotere-comptables .nav{padding-block:12px;}
          .clotere-comptables .brand{font-size:16px;gap:7px;}
          .clotere-comptables .brand-mark{width:26px;height:26px;border-radius:7px;}
          .clotere-comptables .nav-cta .btn{padding:9px 16px;font-size:13px;white-space:nowrap;}
          .clotere-comptables .nav-cta .cta-full{display:none;}
          .clotere-comptables .nav-cta .cta-short{display:inline;}
        }

        /* Hero */
        .clotere-comptables .hero{padding-top:clamp(40px,6vw,72px);padding-bottom:clamp(28px,4vw,40px);}
        .clotere-comptables .trust-row{display:flex;align-items:center;gap:12px;margin-bottom:20px;}
        .clotere-comptables .avatar-cluster{display:flex;}
        .clotere-comptables .avatar-cluster span{
          width:28px;height:28px;border-radius:50%;border:2px solid var(--paper);
          background:linear-gradient(155deg,var(--accent),var(--gold));margin-left:-9px;
        }
        .clotere-comptables .avatar-cluster span:first-child{margin-left:0;}
        .clotere-comptables .trust-text{font-size:12.5px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:var(--ink-soft);}

        .clotere-comptables .hero-top{
          display:grid;grid-template-columns:minmax(0,1.35fr) minmax(0,.8fr);gap:clamp(28px,5vw,64px);
          align-items:start;
        }
        @media (max-width:880px){.clotere-comptables .hero-top{grid-template-columns:minmax(0,1fr);}}
        .clotere-comptables .hero-top h1{
          font-size:clamp(32px,4.6vw,54px);
          line-height:1.1;letter-spacing:-.015em;font-weight:500;
        }
        .clotere-comptables .hero-sub{margin-top:18px;font-size:clamp(15.5px,1.4vw,17px);line-height:1.6;max-width:56ch;}
        .clotere-comptables .hero-offer{padding-top:6px;}
        .clotere-comptables .hero-offer .eyebrow{margin-bottom:16px;}
        .clotere-comptables .hero-offer-list{list-style:none;margin:0 0 22px;padding:0;display:flex;flex-direction:column;gap:11px;}
        .clotere-comptables .hero-offer-list li{display:flex;align-items:flex-start;gap:9px;font-size:15px;font-weight:500;color:var(--ink);}
        .clotere-comptables .hero-offer-list svg{flex:none;color:var(--accent);margin-top:2px;}
        .clotere-comptables .hero-actions{display:flex;flex-wrap:wrap;align-items:flex-start;gap:12px 14px;}
        .clotere-comptables .hero-cta-main{display:flex;flex-direction:column;}
        .clotere-comptables .hero-helper{display:flex;align-items:center;gap:7px;margin-top:10px;font-size:12.5px;color:var(--ink-soft);}
        @media (max-width:480px){
          .clotere-comptables .hero-actions,.clotere-comptables .hero-cta-main{width:100%;}
          .clotere-comptables .hero-actions .btn{width:100%;}
          .clotere-comptables .hero-helper{justify-content:center;}
        }

        /* Hero visual */
        .clotere-comptables .hero-visual{
          position:relative;margin-top:clamp(36px,6vw,56px);
          border-radius:var(--radius-l);overflow:hidden;border:1px solid var(--line);
          background:linear-gradient(160deg,var(--accent-soft),var(--surface-2));
          padding:clamp(20px,4vw,36px);
          min-height:clamp(300px,34vw,420px);
          display:flex;align-items:center;
        }
        .clotere-comptables .mock{
          background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-l);
          box-shadow:var(--shadow);overflow:hidden;width:min(560px,100%);margin-inline:auto;
        }
        .clotere-comptables .mock-bar{
          display:flex;align-items:center;gap:8px;padding:11px 14px;border-bottom:1px solid var(--line);
          background:var(--surface-2);
        }
        .clotere-comptables .mock-dot{width:8px;height:8px;border-radius:50%;background:var(--line);}
        .clotere-comptables .mock-url{
          flex:1;margin-left:6px;background:var(--paper);border:1px solid var(--line);border-radius:7px;
          padding:6px 10px;font-size:12px;color:var(--ink-soft);font-family:ui-monospace,monospace;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
        }
        .clotere-comptables .mock-url b{color:var(--accent-strong);font-weight:600;}
        .clotere-comptables .mock-body{padding:22px;}
        .clotere-comptables .mock-hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
        .clotere-comptables .mock-logo{display:flex;align-items:center;gap:8px;font-weight:800;font-family:'Manrope';font-size:14px;}
        .clotere-comptables .mock-avatar{width:26px;height:26px;border-radius:50%;background:linear-gradient(155deg,var(--accent),var(--gold));}
        .clotere-comptables .mock-cta{background:var(--accent);color:#fff;font-size:11.5px;font-weight:700;padding:7px 12px;border-radius:999px;}
        .clotere-comptables .mock-hero{background:var(--accent-soft);border-radius:var(--radius-m);padding:18px;margin-bottom:14px;}
        .clotere-comptables .mock-hero .bar{height:9px;border-radius:5px;background:color-mix(in srgb, var(--accent) 35%, transparent);margin-bottom:8px;}
        .clotere-comptables .mock-hero .bar.w60{width:60%;}
        .clotere-comptables .mock-hero .bar.w40{width:40%;background:color-mix(in srgb, var(--accent) 20%, transparent);}
        .clotere-comptables .mock-cal{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px;}
        .clotere-comptables .mock-cal div{aspect-ratio:1;border-radius:6px;background:var(--surface-2);border:1px solid var(--line);}
        .clotere-comptables .mock-cal div.on{background:var(--accent);border-color:var(--accent);}
        .clotere-comptables .mock-cal div.gold{background:var(--gold-soft);border-color:var(--gold);}
        .clotere-comptables .mock-foot{display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-top:14px;font-size:11px;color:var(--ink-soft);}

        /* Hero : widget de qualification en 3 étapes */
        .clotere-comptables .qual-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;}
        .clotere-comptables .qual-kicker{font-size:12px;font-weight:700;color:var(--ink);}
        .clotere-comptables .qual-stepper{display:flex;align-items:center;gap:6px;list-style:none;margin:0;padding:0;}
        .clotere-comptables .qual-stepper li{display:flex;align-items:center;gap:6px;font-size:11px;color:var(--ink-soft);}
        .clotere-comptables .qual-stepper li + li::before{content:"·";font-weight:700;color:var(--ink-soft);}
        .clotere-comptables .qual-num{
          width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;
          font-family:'Manrope';font-weight:800;font-size:11px;
          background:var(--surface-2);color:var(--ink-soft);border:1px solid var(--line);
          transition:background .3s ease,color .3s ease,border-color .3s ease;
        }
        .clotere-comptables .qual-num.done{background:var(--accent-soft);color:var(--accent-strong);border-color:var(--accent-soft);}
        .clotere-comptables .qual-num.on{background:var(--accent);color:#fff;border-color:var(--accent);}
        .clotere-comptables .qual-panels{display:grid;}
        .clotere-comptables .qual-panels > .qual-panel{
          grid-area:1/1;opacity:0;visibility:hidden;transform:translateY(6px);
          transition:opacity .45s ease,transform .45s ease,visibility .45s;
        }
        .clotere-comptables .qual-panels > .qual-panel.on{opacity:1;visibility:visible;transform:none;}
        .clotere-comptables .qual-panels.static{gap:14px;}
        .clotere-comptables .qual-panels.static > .qual-panel{grid-area:auto;opacity:1;visibility:visible;transform:none;}
        .clotere-comptables .qual-panels.static > .qual-panel + .qual-panel{border-top:1px solid var(--line);padding-top:14px;}
        .clotere-comptables .qual-title{font-family:'Manrope';font-size:14px;font-weight:800;color:var(--ink);margin-bottom:12px;}
        .clotere-comptables .qual-title span{color:var(--accent-strong);}
        .clotere-comptables .qual-chips{display:flex;flex-wrap:wrap;gap:7px;}
        .clotere-comptables .qual-chip{
          display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;color:var(--ink);
          background:var(--surface);border:1px solid var(--line);padding:6px 11px;border-radius:999px;
        }
        .clotere-comptables .qual-chip.on{background:var(--accent);border-color:var(--accent);color:#fff;}
        .clotere-comptables .qual-fields{display:flex;flex-direction:column;gap:8px;}
        .clotere-comptables .qual-field{
          display:flex;align-items:center;justify-content:space-between;gap:10px;
          border:1px solid var(--line);border-radius:var(--radius-s);padding:9px 12px;background:var(--surface);
        }
        .clotere-comptables .qual-field .qf-label{font-size:11.5px;color:var(--ink-soft);}
        .clotere-comptables .qual-field .qf-value{display:flex;align-items:center;gap:6px;font-size:12.5px;font-weight:700;color:var(--ink);white-space:nowrap;}
        .clotere-comptables .qual-field .qf-value svg{color:var(--ink-soft);}
        .clotere-comptables .qual-slots{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;}
        .clotere-comptables .qual-slot{
          border:1px solid var(--line);border-radius:var(--radius-s);padding:8px 4px;text-align:center;background:var(--surface);
        }
        .clotere-comptables .qual-slot .qs-day{display:block;font-size:11px;color:var(--ink-soft);}
        .clotere-comptables .qual-slot .qs-time{display:block;font-size:13px;font-weight:700;color:var(--ink);}
        .clotere-comptables .qual-slot.on{background:var(--accent);border-color:var(--accent);}
        .clotere-comptables .qual-slot.on .qs-day,.clotere-comptables .qual-slot.on .qs-time{color:#fff;}
        @media (max-width:420px){
          .clotere-comptables .qual-kicker{display:none;}
          .clotere-comptables .qual-head{justify-content:flex-start;}
          .clotere-comptables .mock-body{padding:16px;}
        }

        .clotere-comptables .overlay-card{
          position:absolute;background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-m);
          box-shadow:var(--shadow);padding:14px 16px;width:196px;
        }
        .clotere-comptables .overlay-card.card-rdv{left:clamp(8px,3vw,40px);bottom:-22px;}
        .clotere-comptables .overlay-card.card-rank{right:clamp(8px,3vw,40px);top:22px;}
        .clotere-comptables .overlay-card .oc-title{display:flex;align-items:center;gap:6px;font-size:12.5px;font-weight:700;color:var(--ink);}
        .clotere-comptables .overlay-card .oc-title svg{color:var(--accent);}
        .clotere-comptables .overlay-card .oc-row{display:flex;justify-content:space-between;font-size:10.5px;color:var(--ink-soft);margin-top:10px;}
        .clotere-comptables .overlay-card .oc-bar{height:7px;border-radius:4px;background:var(--surface-2);margin-top:4px;}
        .clotere-comptables .overlay-card .oc-bar.w70{width:70%;}
        .clotere-comptables .overlay-card .oc-bar.w45{width:45%;}
        .clotere-comptables .donut{width:40px;height:40px;border-radius:50%;flex:none;
          background:conic-gradient(var(--accent) 0% 78%, var(--surface-2) 78% 100%);
          display:flex;align-items:center;justify-content:center;}
        .clotere-comptables .donut::after{content:"";width:24px;height:24px;border-radius:50%;background:var(--surface);}
        .clotere-comptables .rank-row{display:flex;align-items:center;gap:10px;}
        .clotere-comptables .rank-label{font-size:12.5px;font-weight:700;color:var(--ink);line-height:1.3;}
        .clotere-comptables .rank-sub{font-size:10.5px;color:var(--ink-soft);}
        @media (max-width:640px){
          .clotere-comptables .hero-visual{flex-direction:column;align-items:stretch;min-height:0;}
          .clotere-comptables .overlay-card{position:static;width:100%;margin-top:14px;}
          .clotere-comptables .hero-visual > .mock{order:1;}
        }

        .clotere-comptables .hero-pills{display:flex;flex-wrap:wrap;gap:8px;margin-top:clamp(28px,5vw,20px);justify-content:center;}
        .clotere-comptables .hero-pills span{
          font-size:12.5px;font-weight:600;color:var(--ink);background:var(--surface);
          border:1px solid var(--line);padding:7px 14px;border-radius:999px;
        }

        /* Section headers */
        .clotere-comptables .section-head{max-width:640px;margin-bottom:clamp(30px,5vw,48px);}
        .clotere-comptables .section-head h2{font-size:clamp(24px,3vw,34px);letter-spacing:-.015em;margin-top:12px;}
        .clotere-comptables .section-head p{margin-top:12px;font-size:16px;}

        /* Offer / pour-qui style block */
        .clotere-comptables .offer-head{
          display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,.9fr);gap:clamp(24px,5vw,48px);
          align-items:end;margin-bottom:clamp(32px,5vw,48px);
        }
        @media (max-width:820px){.clotere-comptables .offer-head{grid-template-columns:minmax(0,1fr);align-items:start;}}
        .clotere-comptables .offer-head h2{font-size:clamp(28px,3.6vw,42px);line-height:1.14;letter-spacing:-.01em;font-weight:500;margin-top:14px;}
        .clotere-comptables .offer-head p{font-size:15.5px;max-width:38ch;}

        .clotere-comptables .pillars{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px;}
        @media (max-width:860px){.clotere-comptables .pillars{grid-template-columns:minmax(0,1fr);}}
        .clotere-comptables .pillar{
          background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-l);
          overflow:hidden;
        }
        .clotere-comptables .card-visual{
          aspect-ratio:16/11;display:flex;align-items:center;justify-content:center;position:relative;
        }
        .clotere-comptables .card-visual.v1{background:linear-gradient(150deg,var(--accent-soft),color-mix(in srgb, var(--accent) 14%, var(--surface-2)));}
        .clotere-comptables .card-visual.v2{background:linear-gradient(150deg,var(--gold-soft),var(--surface-2));}
        .clotere-comptables .card-visual.v3{background:linear-gradient(150deg,var(--surface-2),var(--accent-soft));}

        /* Richer pillar illustrations */
        .clotere-comptables .illus{position:relative;width:76%;height:70%;display:flex;align-items:center;justify-content:center;}
        .clotere-comptables .illus-card,.clotere-comptables .illus-cal{
          background:var(--surface);border-radius:14px;width:100%;padding:14px 16px;
          box-shadow:0 10px 26px -12px rgba(20,36,28,.3);
        }
        .clotere-comptables .illus-card{transform:rotate(-2.5deg);}
        .clotere-comptables .illus-cal{transform:rotate(2deg);padding:14px;}
        .clotere-comptables .illus-line{height:7px;border-radius:4px;background:var(--surface-2);margin-bottom:8px;}
        .clotere-comptables .illus-line.w80{width:80%;}
        .clotere-comptables .illus-line.w60{width:60%;}
        .clotere-comptables .illus-line.w40{width:40%;margin-bottom:0;}
        .clotere-comptables .illus-result{display:flex;align-items:center;gap:8px;background:var(--accent-soft);border-radius:8px;padding:7px 9px;margin-top:4px;}
        .clotere-comptables .illus-dot{width:8px;height:8px;border-radius:50%;background:var(--accent);flex:none;}
        .clotere-comptables .illus-badge{
          position:absolute;bottom:-8px;right:-8px;width:42px;height:42px;border-radius:50%;
          background:var(--accent);color:#fff;display:flex;align-items:center;justify-content:center;
          box-shadow:0 8px 18px -8px rgba(20,36,28,.45);border:3px solid var(--surface);
        }
        .clotere-comptables .illus-badge svg{width:19px;height:19px;}
        .clotere-comptables .illus-badge.gold{background:var(--gold);}
        .clotere-comptables .illus-cal .cal-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:5px;}
        .clotere-comptables .illus-cal .cal-grid div{aspect-ratio:1;border-radius:5px;background:var(--surface-2);}
        .clotere-comptables .illus-cal .cal-grid div.sel{background:var(--accent);}
        .clotere-comptables .illus-stack{position:relative;width:100%;height:100%;}
        .clotere-comptables .illus-doc{
          position:absolute;background:var(--surface);border-radius:12px;width:82%;padding:12px 14px;
          box-shadow:0 10px 24px -12px rgba(20,36,28,.28);
        }
        .clotere-comptables .illus-doc.back{top:6%;left:16%;transform:rotate(5deg);opacity:.85;}
        .clotere-comptables .illus-doc.front{top:18%;left:2%;transform:rotate(-3.5deg);}
        .clotere-comptables .illus-tag{display:inline-flex;font-size:10px;font-weight:700;color:var(--accent-strong);background:var(--accent-soft);padding:3px 8px;border-radius:999px;margin-bottom:8px;}
        .clotere-comptables .pillar-body{padding:22px 24px 26px;}
        .clotere-comptables .pillar h3{font-size:18.5px;}
        .clotere-comptables .pillar p{font-size:14.5px;margin-top:10px;}

        /* Results / measurable */
        .clotere-comptables .results{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px;}
        @media (max-width:860px){.clotere-comptables .results{grid-template-columns:minmax(0,1fr);}}
        .clotere-comptables .result-card{
          background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-l);
          overflow:hidden;display:flex;flex-direction:column;
        }
        .clotere-comptables .result-visual{aspect-ratio:16/9;display:flex;align-items:center;justify-content:center;}
        .clotere-comptables .result-visual svg{width:46px;height:46px;color:var(--accent-strong);}
        .clotere-comptables .result-visual.v2 svg{color:var(--gold);}
        .clotere-comptables .result-body{padding:22px 22px 24px;display:flex;flex-direction:column;gap:12px;}
        .clotere-comptables .result-body h4{font-size:16.5px;}
        .clotere-comptables .result-body p{font-size:13.5px;}
        .clotere-comptables .spark{width:100%;height:34px;margin-top:2px;}

        /* Compliance header + stats (figures-row style) */
        .clotere-comptables .compliance-head{display:grid;grid-template-columns:200px minmax(0,1fr);gap:24px;align-items:start;}
        @media (max-width:700px){.clotere-comptables .compliance-head{grid-template-columns:minmax(0,1fr);gap:14px;}}
        .clotere-comptables .compliance-head .eyebrow{margin-top:6px;}
        .clotere-comptables .compliance-head h2{font-size:clamp(24px,3.2vw,36px);line-height:1.25;font-weight:500;letter-spacing:-.005em;}
        .clotere-comptables .compliance-stats{display:flex;flex-wrap:wrap;margin-top:clamp(32px,5vw,48px);}
        .clotere-comptables .compliance-stat{flex:1;min-width:200px;padding:0 clamp(20px,3vw,36px);border-left:1px solid var(--line);}
        .clotere-comptables .compliance-stat:first-child{padding-left:0;border-left:none;}
        @media (max-width:700px){
          .clotere-comptables .compliance-stat{border-left:none;border-top:1px solid var(--line);padding:24px 0 0;flex:1 1 100%;}
          .clotere-comptables .compliance-stat:first-child{border-top:none;padding-top:0;}
        }
        .clotere-comptables .compliance-stat .stat-word{font-family:'Newsreader';font-weight:600;font-size:clamp(24px,2.8vw,32px);color:var(--ink);letter-spacing:-.01em;}
        .clotere-comptables .compliance-stat p{margin-top:10px;font-size:14px;max-width:30ch;}

        .clotere-comptables .check-badge{
          flex:none;width:22px;height:22px;border-radius:50%;background:var(--mark-bg);color:var(--mark-ink);
          display:flex;align-items:center;justify-content:center;
        }

        /* Parcours client: left timeline + right illustration */
        .clotere-comptables .parcours-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:clamp(28px,5vw,64px);align-items:center;}
        @media (max-width:880px){.clotere-comptables .parcours-grid{grid-template-columns:minmax(0,1fr);}}
        .clotere-comptables .timeline{display:flex;flex-direction:column;}
        .clotere-comptables .tl-item{display:grid;grid-template-columns:52px minmax(0,1fr);gap:16px;position:relative;padding-bottom:30px;}
        .clotere-comptables .tl-item:last-child{padding-bottom:0;}
        .clotere-comptables .tl-item .tl-icon{
          width:52px;height:52px;border-radius:50%;background:var(--accent-soft);color:var(--accent-strong);
          display:flex;align-items:center;justify-content:center;position:relative;z-index:1;flex:none;
        }
        .clotere-comptables .tl-item .tl-icon svg{width:22px;height:22px;}
        .clotere-comptables .tl-item .tl-badge{
          position:absolute;top:-4px;right:-4px;width:18px;height:18px;border-radius:50%;
          background:var(--accent);color:#fff;font-family:'Manrope';font-weight:800;font-size:10px;
          display:flex;align-items:center;justify-content:center;border:2px solid var(--surface);
        }
        .clotere-comptables .tl-item .tl-line{
          position:absolute;left:26px;top:52px;bottom:0;width:2px;background:var(--line);
        }
        .clotere-comptables .tl-item:last-child .tl-line{display:none;}
        .clotere-comptables .tl-body{padding-top:6px;}
        .clotere-comptables .tl-body h4{font-size:16.5px;}
        .clotere-comptables .tl-body p{font-size:14px;margin-top:6px;max-width:46ch;}

        .clotere-comptables .parcours-visual{
          background:linear-gradient(160deg,var(--accent-soft),var(--surface-2));
          border:1px solid var(--line);border-radius:var(--radius-l);padding:clamp(18px,3vw,30px);
        }
        .clotere-comptables .doc-row{display:flex;align-items:center;gap:10px;padding:11px 0;border-bottom:1px solid var(--line);}
        .clotere-comptables .doc-row .doc-icon{width:32px;height:32px;border-radius:8px;background:var(--accent-soft);color:var(--accent-strong);display:flex;align-items:center;justify-content:center;flex:none;}
        .clotere-comptables .doc-row .doc-icon svg{width:16px;height:16px;}
        .clotere-comptables .doc-row .doc-name{flex:1;font-size:12.5px;font-weight:600;color:var(--ink);}
        .clotere-comptables .doc-row .doc-status{font-size:10.5px;font-weight:700;color:var(--accent-strong);background:var(--accent-soft);padding:4px 9px;border-radius:999px;white-space:nowrap;}
        .clotere-comptables .stars-row{display:flex;align-items:center;gap:10px;margin-top:14px;padding-top:14px;border-top:1px solid var(--line);}
        .clotere-comptables .stars{display:flex;gap:2px;color:var(--gold);flex:none;}
        .clotere-comptables .stars svg{width:15px;height:15px;}
        .clotere-comptables .stars-row span{font-size:12px;color:var(--ink-faint);}

        /* Pricing : deux formules, aucun prix affiché */
        .clotere-comptables .plans{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:clamp(18px,3vw,28px);align-items:stretch;}
        @media (max-width:820px){.clotere-comptables .plans{grid-template-columns:minmax(0,1fr);gap:28px;}}
        .clotere-comptables .plan{
          position:relative;display:flex;flex-direction:column;
          background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-l);
          padding:clamp(24px,3.5vw,36px);
        }
        .clotere-comptables .plan.featured{border:2px solid var(--accent);box-shadow:var(--shadow);}
        .clotere-comptables .plan-badge{
          position:absolute;top:0;left:clamp(24px,3.5vw,36px);transform:translateY(-50%);
          background:var(--accent);color:#fff;font-size:12px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
          padding:5px 12px;border-radius:999px;
        }
        .clotere-comptables .plan h3{font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--accent-strong);}
        .clotere-comptables .plan-pitch{margin-top:10px;font-family:'Newsreader',Georgia,serif;font-weight:500;font-size:clamp(22px,2.4vw,27px);line-height:1.2;color:var(--ink);}
        .clotere-comptables .plan-list{list-style:none;margin:24px 0 0;padding:0;display:flex;flex-direction:column;gap:12px;}
        .clotere-comptables .plan-list li{display:flex;gap:10px;font-size:14.5px;line-height:1.45;color:var(--ink);align-items:flex-start;}
        .clotere-comptables .plan-list .check-badge{margin-top:1px;}
        .clotere-comptables .plan-list li.plan-lead{font-weight:700;}
        .clotere-comptables .plan-cta{padding-top:28px;margin-top:auto;}
        .clotere-comptables .plan-option{
          margin-top:clamp(20px,3vw,28px);display:grid;grid-template-columns:200px minmax(0,1fr);gap:8px 24px;align-items:baseline;
          background:var(--surface-2);border:1px solid var(--line);border-radius:var(--radius-m);
          padding:clamp(18px,3vw,24px) clamp(20px,3.5vw,32px);
        }
        @media (max-width:700px){.clotere-comptables .plan-option{grid-template-columns:minmax(0,1fr);}}
        .clotere-comptables .plan-option-label{font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--accent-strong);}
        .clotere-comptables .plan-option h3{font-size:17px;font-weight:700;}
        .clotere-comptables .plan-option p{margin-top:6px;font-size:14.5px;max-width:70ch;}
        /* Final CTA + contact */
        .clotere-comptables .cta-band{
          background:var(--accent-strong);color:#fff;border-radius:var(--radius-l);
          padding:clamp(32px,5vw,56px);display:grid;grid-template-columns:minmax(0,1.1fr) minmax(0,.9fr);gap:36px;
        }
        @media (max-width:880px){.clotere-comptables .cta-band{grid-template-columns:minmax(0,1fr);}}
        .clotere-comptables .cta-band h2{color:#fff;font-size:clamp(24px,3vw,32px);letter-spacing:-.015em;}
        .clotere-comptables .cta-band p{color:color-mix(in srgb, #fff 78%, transparent);margin-top:12px;font-size:15.5px;max-width:46ch;}
        .clotere-comptables .cta-band .hero-chips{display:flex;flex-wrap:wrap;gap:10px 22px;margin-top:24px;}
        .clotere-comptables .cta-band .hero-chip{display:flex;align-items:center;gap:8px;font-size:13.5px;font-weight:500;color:color-mix(in srgb, #fff 82%, transparent);}
        .clotere-comptables .cta-band .hero-chip svg{flex:none;color:var(--gold);}

        .clotere-comptables .form-card{background:var(--paper);color:var(--ink);border-radius:var(--radius-m);padding:22px;}
        .clotere-comptables .field{margin-bottom:13px;}
        .clotere-comptables .field label{display:block;font-size:12.5px;font-weight:600;color:var(--ink-soft);margin-bottom:6px;}
        .clotere-comptables .field input,.clotere-comptables .field select{
          width:100%;padding:10px 12px;border-radius:8px;border:1px solid var(--line);
          background:var(--surface);color:var(--ink);font-size:14.5px;font-family:inherit;
        }
        .clotere-comptables .field input:focus,.clotere-comptables .field select:focus{outline:2px solid var(--accent);outline-offset:1px;}
        .clotere-comptables .form-note{font-size:12px;color:var(--ink-faint);margin-top:10px;}
        /* Le formulaire vit dans .cta-band (fond sombre, texte clair par défaut) mais
           sa carte à lui est claire : on doit re-surspécifier la couleur du texte ici,
           sinon la règle .cta-band p (texte blanc) s'applique et devient illisible. */
        .clotere-comptables .cta-band .form-card p{color:var(--ink-soft);margin-top:0;max-width:none;}
        .clotere-comptables .cta-band .form-card .form-note{color:var(--ink-faint);}
        .clotere-comptables .form-success{display:flex;flex-direction:column;align-items:center;text-align:center;padding:clamp(30px,5vw,44px) 22px;}
        .clotere-comptables .form-success-icon{
          width:60px;height:60px;border-radius:50%;flex:none;
          background:var(--accent);color:#fff;
          display:flex;align-items:center;justify-content:center;
          margin-bottom:18px;
          box-shadow:0 10px 24px -10px color-mix(in srgb, var(--accent) 55%, transparent);
        }
        .clotere-comptables .form-success h3{font-size:24px;letter-spacing:-.01em;}
        .clotere-comptables .cta-band .form-success p{margin-top:8px;font-size:15.5px;max-width:30ch;}

        .clotere-comptables footer{border-top:1px solid var(--line);}
        .clotere-comptables .footer-grid{display:flex;flex-wrap:wrap;justify-content:space-between;gap:20px;padding-block:32px;font-size:13px;color:var(--ink-faint);}
        .clotere-comptables .footer-links{display:flex;gap:18px;}
        .clotere-comptables .footer-links a{text-decoration:none;color:var(--ink-faint);}
        .clotere-comptables .footer-links a:hover{color:var(--ink);}

        .clotere-comptables .visually-hidden{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;}
        .clotere-comptables :focus-visible{outline:2px solid var(--accent);outline-offset:2px;}
        @media (prefers-reduced-motion:reduce){.clotere-comptables *{transition:none!important;}}
      `}</style>

      <div className="clotere-comptables">
        <header>
          <div className="wrap nav">
            <div className="brand">
              <img src={logo} alt="Comptelio" className="brand-mark" />
              Comptelio
            </div>
            <nav className="nav-links">
              <a href="#offre">L'offre</a>
              <a href="#resultats">Résultats</a>
              <a href="#parcours">Parcours client</a>
              <a href="#conformite">Conformité</a>
              <a href="#tarif">Tarif</a>
            </nav>
            <div className="nav-cta">
              <a href="#contact" className="btn btn-primary">
                <span className="cta-full">Réserver une démo</span>
                <span className="cta-short">Démo</span>
              </a>
            </div>
          </div>
        </header>

        <main>
          <section className="hero">
            <div className="wrap">
              <div className="hero-top">
                <div>
                  <div className="trust-row">
                    <div className="avatar-cluster"><span></span><span></span><span></span></div>
                    <span className="trust-text">Experts-comptables · Professions réglementées</span>
                  </div>
                  <h1 className="serif">Plus de visibilité, plus de rendez-vous, <span className="mark">plus de clients</span>.</h1>
                  <p className="hero-sub">Pour les professions réglementées. Comptelio fait trouver votre cabinet sur Google et LinkedIn, et transforme chaque visite en rendez-vous qualifié : vous savez qui vient, et pour quelle mission, avant même le premier échange.</p>
                </div>
                <div className="hero-offer">
                  <span className="eyebrow">Pour votre cabinet</span>
                  <ul className="hero-offer-list">
                    <li><CheckIcon size={18} width={2.6} />Rendez-vous qualifiés, 24h/24</li>
                    <li><CheckIcon size={18} width={2.6} />Fiche Google et LinkedIn qui convertissent</li>
                    <li><CheckIcon size={18} width={2.6} />Avis Google récoltés après chaque rendez-vous</li>
                  </ul>
                  <div className="hero-actions">
                    <div className="hero-cta-main">
                      <a href="#contact" className="btn btn-dark">Réserver une démo</a>
                      <div className="hero-helper">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" /><path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                        15 minutes, sans engagement
                      </div>
                    </div>
                    <a href="#parcours" className="btn btn-ghost">Voir comment ça marche <span aria-hidden="true">→</span></a>
                  </div>
                </div>
              </div>

              {/* Illustration décorative : masquée aux lecteurs d'écran (contenu animé) */}
              <div className="hero-visual" aria-hidden="true">
                <div className="mock">
                  <div className="mock-bar">
                    <span className="mock-dot"></span><span className="mock-dot"></span><span className="mock-dot"></span>
                    <span className="mock-url">dupont.<b>monexpertcomptable</b>.fr</span>
                  </div>
                  <div className="mock-body">
                    <div className="qual-head">
                      <div className="mock-logo"><span className="mock-avatar"></span><span className="qual-kicker">Cabinet Dupont</span></div>
                      <ol className="qual-stepper">
                        <li><span className={stepClass(0)}>1</span></li>
                        <li><span className={stepClass(1)}>2</span></li>
                        <li><span className={stepClass(2)}>3</span></li>
                      </ol>
                    </div>
                    <div className={reduceMotion ? 'qual-panels static' : 'qual-panels'}>
                      <div className={panelClass(0)}>
                        <div className="qual-title"><span>1.</span> Votre besoin</div>
                        <div className="qual-chips">
                          <span className="qual-chip on"><CheckIcon size={11} />Création d'entreprise</span>
                          <span className="qual-chip">Tenue comptable</span>
                          <span className="qual-chip">Fiscalité / LMNP</span>
                          <span className="qual-chip">Changement d'expert-comptable</span>
                        </div>
                      </div>
                      <div className={panelClass(1)}>
                        <div className="qual-title"><span>2.</span> Votre situation</div>
                        <div className="qual-fields">
                          <div className="qual-field">
                            <span className="qf-label">Statut</span>
                            <span className="qf-value">SAS<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                          </div>
                          <div className="qual-field">
                            <span className="qf-label">CA estimé</span>
                            <span className="qf-value">Moins de 100 k€<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                          </div>
                        </div>
                      </div>
                      <div className={panelClass(2)}>
                        <div className="qual-title"><span>3.</span> Choisissez votre créneau</div>
                        <div className="qual-slots">
                          <div className="qual-slot"><span className="qs-day">Mardi</span><span className="qs-time">9:30</span></div>
                          <div className="qual-slot on"><span className="qs-day">Mercredi</span><span className="qs-time">14:00</span></div>
                          <div className="qual-slot"><span className="qs-day">Jeudi</span><span className="qs-time">11:00</span></div>
                        </div>
                      </div>
                    </div>
                    <div className="mock-foot"><span>Qualification en 3 étapes</span><span>Synchronisé LinkedIn · Google</span></div>
                  </div>
                </div>

                <div className="overlay-card card-rdv">
                  <div className="oc-title"><svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 12l5 5L20 6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>Rendez-vous confirmé</div>
                  <div className="oc-row"><span>Date</span><span>Créneau</span></div>
                  <div className="oc-bar w70"></div>
                  <div className="oc-bar w45" style={{ marginTop: '8px' }}></div>
                </div>

                <div className="overlay-card card-rank">
                  <div className="rank-row">
                    <div className="donut"></div>
                    <div>
                      <div className="rank-label">Position Google</div>
                      <div className="rank-sub">Objectif : top 5 local</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hero-pills">
                <span>TPE</span><span>PME</span><span>Indépendants</span><span>Professions libérales</span><span>Associations</span>
              </div>
            </div>
          </section>

          <section id="offre">
            <div className="wrap">
              <div className="offer-head">
                <div>
                  <span className="eyebrow">L'offre</span>
                  <h2 className="serif">Une <span className="mark">visibilité sans faille</span> pour capter des clients</h2>
                </div>
                <p>Trois briques complémentaires pour attirer des visiteurs et les transformer en clients.</p>
              </div>
              <div className="pillars">
                <div className="pillar">
                  <div className="card-visual v1">
                    <div className="illus">
                      <div className="illus-card">
                        <div className="illus-line w80"></div>
                        <div className="illus-line w60"></div>
                        <div className="illus-result"><span className="illus-dot"></span><div className="illus-line w40"></div></div>
                      </div>
                      <div className="illus-badge"><svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" /><path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></div>
                    </div>
                  </div>
                  <div className="pillar-body">
                    <h3>Site internet &amp; référencement</h3>
                    <p>Un site sobre et rapide, hébergé chez nous sur votre sous-domaine dédié, optimisé pour apparaître dans les recherches de votre ville.</p>
                  </div>
                </div>
                <div className="pillar">
                  <div className="card-visual v2">
                    <div className="illus">
                      <div className="illus-cal">
                        <div className="cal-grid">
                          <div></div><div></div><div></div><div></div><div></div><div></div>
                          <div></div><div className="sel"></div><div></div><div></div><div></div><div></div>
                          <div></div><div></div><div></div><div></div><div></div><div></div>
                        </div>
                      </div>
                      <div className="illus-badge gold"><svg viewBox="0 0 24 24" fill="none"><path d="M4 12l5 5L20 6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                    </div>
                  </div>
                  <div className="pillar-body">
                    <h3>Prise de rendez-vous en ligne</h3>
                    <p>Connectée à votre agenda et poussée sur votre site, LinkedIn et votre fiche Google Business, pour ne rater aucune demande.</p>
                  </div>
                </div>
                <div className="pillar">
                  <div className="card-visual v3">
                    <div className="illus">
                      <div className="illus-stack">
                        <div className="illus-doc back">
                          <div className="illus-line w80"></div>
                          <div className="illus-line w60"></div>
                        </div>
                        <div className="illus-doc front">
                          <span className="illus-tag">SEO</span>
                          <div className="illus-line w80"></div>
                          <div className="illus-line w60"></div>
                          <div className="illus-line w40"></div>
                        </div>
                      </div>
                      <div className="illus-badge"><svg viewBox="0 0 24 24" fill="none"><path d="M4 20l1-4.2L15.6 5.2a1.5 1.5 0 012.1 0l1.1 1.1a1.5 1.5 0 010 2.1L8.2 19 4 20z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg></div>
                    </div>
                  </div>
                  <div className="pillar-body">
                    <h3>Contenu optimisé</h3>
                    <p>Des articles publiés chaque mois, ciblés sur votre métier et votre zone de chalandise, pour nourrir votre référencement dans la durée.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="resultats">
            <div className="wrap">
              <div className="section-head">
                <span className="eyebrow">Reporting</span>
                <h2 className="serif">Des <span className="mark">résultats mesurés</span>, trimestre après trimestre</h2>
                <p>Chaque trimestre, un point chiffré sur ce qui fait avancer la visibilité de votre cabinet.</p>
              </div>
              <div className="results">
                <div className="result-card">
                  <div className="result-visual v1"><svg viewBox="0 0 24 24" fill="none"><rect x="3" y="4.5" width="18" height="15.5" rx="2.5" stroke="currentColor" strokeWidth="1.5" /><path d="M3 9.5h18M8 3v3M16 3v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><path d="M8 14.5l2.4 2.4L16 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                  <div className="result-body">
                    <h4 className="font-bold">Prises de rendez-vous</h4>
                    <p>Le nombre de demandes générées depuis votre site, LinkedIn et votre fiche Google Business.</p>
                    <svg className="spark" viewBox="0 0 160 34" preserveAspectRatio="none"><path d="M2 26 L28 22 L54 24 L80 14 L106 16 L132 6 L158 8" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
                <div className="result-card">
                  <div className="result-visual v2"><svg viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-5.2-7-11a7 7 0 0114 0c0 5.8-7 11-7 11z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><circle cx="12" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.5" /></svg></div>
                  <div className="result-body">
                    <h4 className="font-bold">Position sur Google</h4>
                    <p>L'évolution de votre place sur les recherches locales liées à votre métier et votre ville.</p>
                    <svg className="spark" viewBox="0 0 160 34" preserveAspectRatio="none"><path d="M2 10 L28 14 L54 12 L80 20 L106 18 L132 28 L158 26" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
                <div className="result-card">
                  <div className="result-visual v3"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.8l-5.2 2.9 1-5.9-4.3-4.1 5.9-.8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg></div>
                  <div className="result-body">
                    <h4 className="font-bold">Avis clients obtenus</h4>
                    <p>Le nombre de nouveaux avis récoltés sur votre fiche Google Business chaque mois.</p>
                    <svg className="spark" viewBox="0 0 160 34" preserveAspectRatio="none"><path d="M2 28 L28 24 L54 25 L80 16 L106 12 L132 10 L158 4" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="parcours">
            <div className="wrap">
              <div className="section-head">
                <span className="eyebrow">Parcours client</span>
                <h2 className="serif">Votre client se sent <span className="mark">pris en charge</span> dès le début</h2>
                <p>De la recherche sur Google au premier avis laissé, chaque étape est pensée pour rassurer.</p>
              </div>
              <div className="parcours-grid">
                <div className="timeline">
                  <div className="tl-item">
                    <div className="tl-icon">
                      <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" /><path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      <span className="tl-badge">1</span>
                    </div>
                    <div className="tl-line"></div>
                    <div className="tl-body">
                      <h4 className="font-bold">Il vous trouve sur Google</h4>
                      <p>Grâce à votre site, votre fiche Google Business et vos articles, il tombe sur votre cabinet en cherchant un expert-comptable près de chez lui.</p>
                    </div>
                  </div>
                  <div className="tl-item">
                    <div className="tl-icon">
                      <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="4.5" width="18" height="15.5" rx="2.5" stroke="currentColor" strokeWidth="1.8" /><path d="M3 9.5h18M8 3v3M16 3v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M8 14l2.5 2.5L16 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      <span className="tl-badge">2</span>
                    </div>
                    <div className="tl-line"></div>
                    <div className="tl-body">
                      <h4 className="font-bold">Il prend rendez-vous dans votre agenda</h4>
                      <p>En quelques clics, directement synchronisé avec vos disponibilités réelles.</p>
                    </div>
                  </div>
                  <div className="tl-item">
                    <div className="tl-icon">
                      <svg viewBox="0 0 24 24" fill="none"><path d="M4 7a2 2 0 012-2h4l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M12 10v6M9.5 12.5L12 10l2.5 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      <span className="tl-badge">3</span>
                    </div>
                    <div className="tl-line"></div>
                    <div className="tl-body">
                      <h4 className="font-bold">Il accède à un espace client pour déposer ses documents</h4>
                      <p>Avant même le premier rendez-vous, il peut transmettre ses pièces en toute sécurité.</p>
                    </div>
                  </div>
                  <div className="tl-item">
                    <div className="tl-icon">
                      <svg viewBox="0 0 24 24" fill="none"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.8l-5.2 2.9 1-5.9-4.3-4.1 5.9-.8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
                      <span className="tl-badge">4</span>
                    </div>
                    <div className="tl-body">
                      <h4 className="font-bold">Il dépose un avis positif sur votre fiche Google</h4>
                      <p>Une expérience fluide du début à la fin, qui se traduit en avis et en recommandations.</p>
                    </div>
                  </div>
                </div>

                <div className="parcours-visual">
                  <div className="mock">
                    <div className="mock-bar">
                      <span className="mock-dot"></span><span className="mock-dot"></span><span className="mock-dot"></span>
                      <span className="mock-url">espace-client.<b>monexpertcomptable</b>.fr</span>
                    </div>
                    <div className="mock-body">
                      <div className="mock-hd">
                        <div className="mock-logo"><span className="mock-avatar"></span>Espace client</div>
                      </div>
                      <div className="doc-row">
                        <div className="doc-icon"><svg viewBox="0 0 24 24" fill="none"><path d="M6 3h9l5 5v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg></div>
                        <span className="doc-name">Pièce d'identité.pdf</span>
                        <span className="doc-status">Reçu</span>
                      </div>
                      <div className="doc-row" style={{ borderBottom: 'none' }}>
                        <div className="doc-icon"><svg viewBox="0 0 24 24" fill="none"><path d="M6 3h9l5 5v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg></div>
                        <span className="doc-name">Justificatif de domicile.pdf</span>
                        <span className="doc-status">Reçu</span>
                      </div>
                      <div className="stars-row">
                        <div className="stars">
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.8l-5.2 2.9 1-5.9-4.3-4.1 5.9-.8z" /></svg>
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.8l-5.2 2.9 1-5.9-4.3-4.1 5.9-.8z" /></svg>
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.8l-5.2 2.9 1-5.9-4.3-4.1 5.9-.8z" /></svg>
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.8l-5.2 2.9 1-5.9-4.3-4.1 5.9-.8z" /></svg>
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.8l-5.2 2.9 1-5.9-4.3-4.1 5.9-.8z" /></svg>
                        </div>
                        <span>Avis déposé sur votre fiche Google</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="conformite">
            <div className="wrap">
              <div className="compliance-head">
                <span className="eyebrow">Sécurité &amp; conformité</span>
                <h2 className="serif">Votre visibilité <span className="mark">conforme aux exigences</span> de votre profession, du site à l'hébergement de vos données.</h2>
              </div>
              <div className="compliance-stats">
                <div className="compliance-stat">
                  <div className="stat-word">Chiffrées</div>
                  <p>Données cryptées et anonymisées, de bout en bout.</p>
                </div>
                <div className="compliance-stat">
                  <div className="stat-word">100% France</div>
                  <p>Infrastructure hébergée exclusivement sur le territoire français.</p>
                </div>
                <div className="compliance-stat">
                  <div className="stat-word">SecNumCloud</div>
                  <p>Qualification visée pour l'hébergement de vos données sensibles.</p>
                </div>
              </div>
            </div>
          </section>

          <section id="tarif">
            <div className="wrap">
              <div className="section-head">
                <span className="eyebrow">Tarif</span>
                <h2 className="serif">Deux formules pour <span className="mark">remplir votre agenda</span> de bons rendez-vous</h2>
                <p>Un forfait mensuel, sans frais cachés. Le tarif est adapté à votre ville et à vos spécialités : on vous le présente pendant la démo.</p>
              </div>

              <div className="plans">
                <article className="plan">
                  <h3>Essentiel</h3>
                  <p className="plan-pitch">Recevez des rendez-vous qualifiés</p>
                  <ul className="plan-list">
                    <li><span className="check-badge"><CheckIcon /></span>Prise de rendez-vous qualifiée sur votre site, votre fiche Google, LinkedIn et votre signature mail</li>
                    <li><span className="check-badge"><CheckIcon /></span>Questionnaire adapté à vos missions</li>
                    <li><span className="check-badge"><CheckIcon /></span>Rappels automatiques et espace client sécurisé</li>
                    <li><span className="check-badge"><CheckIcon /></span>Demande d'avis Google envoyée après chaque rendez-vous</li>
                  </ul>
                  <div className="plan-cta">
                    <a href="#contact" className="btn btn-dark btn-block" aria-label="Réserver ma démo, formule Essentiel">Réserver ma démo</a>
                  </div>
                </article>

                <article className="plan featured">
                  <span className="plan-badge">Recommandé</span>
                  <h3>Croissance</h3>
                  <p className="plan-pitch">Devenez le cabinet qu'on trouve en premier</p>
                  <ul className="plan-list">
                    <li className="plan-lead">Tout Essentiel, plus :</li>
                    <li><span className="check-badge"><CheckIcon /></span>Stratégie de positionnement : votre ville, vos spécialités</li>
                    <li><span className="check-badge"><CheckIcon /></span>Landing pages dédiées à vos spécialités</li>
                    <li><span className="check-badge"><CheckIcon /></span>Optimisation SEO technique</li>
                    <li><span className="check-badge"><CheckIcon /></span>Articles publiés chaque mois</li>
                    <li><span className="check-badge"><CheckIcon /></span>Reporting trimestriel</li>
                    <li><span className="check-badge"><CheckIcon /></span>Objectif : top 5 dans votre ville ou sur votre spécialité</li>
                  </ul>
                  <div className="plan-cta">
                    <a href="#contact" className="btn btn-primary btn-block" aria-label="Réserver ma démo, formule Croissance">Réserver ma démo</a>
                  </div>
                </article>
              </div>

              <div className="plan-option">
                <span className="plan-option-label">En option</span>
                <div>
                  <h3>Votre site internet</h3>
                  <p>Pas encore de site, ou un site à refaire ? Nous créons un site pensé pour un cabinet d'expertise comptable, hébergé en France et maintenu par nos soins.</p>
                </div>
              </div>            </div>
          </section>

          <section id="contact">
            <div className="wrap">
              <div className="cta-band">
                <div>
                  <span className="eyebrow" style={{ background: 'color-mix(in srgb, #fff 16%, transparent)', color: '#fff' }}>Dernière étape</span>
                  <h2 style={{ marginTop: '14px' }}>Prêt à devenir la <span className="mark">référence de votre ville</span> ?</h2>
                  <p>15 minutes suffisent pour voir à quoi ressemblerait votre site et discuter de votre zone de chalandise.</p>
                  <div className="hero-chips">
                    <span className="hero-chip"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 12l5 5L20 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>Démo sans engagement</span>
                    <span className="hero-chip"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 12l5 5L20 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>Réponse sous 24h ouvrées</span>
                  </div>
                </div>
                {status === 'sent' ? (
                  <div className="form-card form-success">
                    <div className="form-success-icon" aria-hidden="true">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                        <path d="M4 12l5 5L20 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <h3>Merci !</h3>
                    <p>On vous recontacte très vite, sous 24h ouvrées.</p>
                  </div>
                ) : (
                  <form className="form-card" onSubmit={handleSubmit}>
                    <div className="field">
                      <label htmlFor="f-name">Nom du cabinet</label>
                      <input id="f-name" name="name" type="text" required placeholder="Cabinet Dupont" />
                    </div>
                    <div className="field">
                      <label htmlFor="f-city">Ville / département ciblé</label>
                      <input id="f-city" name="city" type="text" required placeholder="Lyon (69)" />
                    </div>
                    <div className="field">
                      <label htmlFor="f-email">Email</label>
                      <input id="f-email" name="email" type="email" required placeholder="vous@cabinet.fr" />
                    </div>
                    <div className="field">
                      <label htmlFor="f-phone">Téléphone (optionnel)</label>
                      <input id="f-phone" name="phone" type="tel" placeholder="06 12 34 56 78" />
                    </div>
                    <button type="submit" className="btn btn-primary btn-block" disabled={status === 'sending'}>
                      {status === 'sending' ? 'Envoi en cours…' : 'Réserver ma démo'}
                    </button>
                    {status === 'error' ? (
                      <p className="form-note" style={{ color: '#E8B4B4' }}>
                        Une erreur est survenue. Vous pouvez aussi nous écrire directement à{' '}
                        <a href="mailto:contact@comptelio.fr">contact@comptelio.fr</a>.
                      </p>
                    ) : (
                      <p className="form-note">Vos informations sont transmises directement à Comptelio, aucune messagerie à ouvrir.</p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </section>
        </main>

        <footer style={{ paddingBottom: 'clamp(24px, 12vw, 64px)' }}>
          <div className="wrap footer-grid">
            <span>© {year} Comptelio</span>
            <div className="footer-links">
              <a href="mailto:contact@comptelio.fr">contact@comptelio.fr</a>
              <a href="https://comptelio.fr" target="_blank" rel="noopener noreferrer">comptelio.fr</a>
            </div>
          </div>
        </footer>
      </div>

      <ChecklistFloatingBanner />
    </>
  );
};

export default ClotereComptables;
